import { isUuid } from './isUuid';
import { z } from 'zod';

const stringSchema = z.string();

export type BrandedUuid<Brand extends string> = string & {
  __type: Brand;
};

export type UnparsedUuid = {
  readonly rawValue: unknown;
};

export type UuidInput = string | UnparsedUuid;

const uuidInputSchema = z.union([z.string(), z.object({ rawValue: z.unknown() })]);
type ParsedUuidInput = z.infer<typeof uuidInputSchema>;

const isRawUuidInput = (input: ParsedUuidInput): input is { rawValue?: unknown } =>
  !stringSchema.safeParse(input).success;

export type BrandedUuidUtil<Brand extends string> = {
  isValid: (id: string) => id is BrandedUuid<Brand>;
  parse: (id: string) => BrandedUuid<Brand>;
  parseUnknown: (input: UuidInput) => BrandedUuid<Brand>;
  parseOrNull: (id: string | null | undefined) => BrandedUuid<Brand> | null;
  parseArray: (ids: string[]) => BrandedUuid<Brand>[];
  parseUnknownArray: (inputs: readonly UnparsedUuid[]) => BrandedUuid<Brand>[];
};

const parseUuid = <Brand extends string>(brand: Brand, input: UuidInput): BrandedUuid<Brand> => {
  const parsedInput = uuidInputSchema.parse(input);
  const rawValue = isRawUuidInput(parsedInput) ? parsedInput.rawValue : parsedInput;
  const parsedId = stringSchema.safeParse(rawValue);
  if (!parsedId.success) {
    throw new Error(`Invalid ${brand} ID: ${rawValue}`);
  }
  if (!isUuid(parsedId.data)) {
    throw new Error(`Invalid ${brand} ID: ${rawValue}`);
  }
  // SAFETY: isUuid verified the string before it is branded.
  return parsedId.data as BrandedUuid<Brand>;
};

const parseUuidArray = <Brand extends string>(brand: Brand, inputs: readonly UnparsedUuid[]): BrandedUuid<Brand>[] => {
  return inputs.map((input) => parseUuid(brand, input));
};

export const buildUuidUtil = <Brand extends string>(brand: Brand): BrandedUuidUtil<Brand> => {
  return {
    isValid: (id: string): id is BrandedUuid<Brand> => isUuid(id),
    parse: (id: string): BrandedUuid<Brand> => parseUuid(brand, { rawValue: id }),
    parseUnknown: (input: UuidInput): BrandedUuid<Brand> => parseUuid(brand, input),
    parseOrNull: (id: string | null | undefined) => {
      if (!id || !isUuid(id)) {
        return null;
      }
      // SAFETY: isUuid verified the string before it is branded.
      return id as BrandedUuid<Brand>;
    },
    parseArray: (ids: string[]): BrandedUuid<Brand>[] =>
      parseUuidArray(
        brand,
        ids.map((id) => ({ rawValue: id }))
      ),
    parseUnknownArray: (inputs: readonly UnparsedUuid[]): BrandedUuid<Brand>[] => parseUuidArray(brand, inputs),
  };
};
